import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { ratePerHaToTotal, totalToRatePerHa } from "common/utils/area";
import { NameAndGuid } from "features/general/NameAndGuidMultiSelectDropdown";
import {
  AMOUNT_INPUT_MODE,
  Field,
  GRASS_TYPE,
  RESEED_TOTAL_AMOUNT_UNIT,
  SEEDING_METHOD,
  useFields,
} from "model";
import { Dispatch, ReactNode, SetStateAction } from "react";
import GrassTypeDropdown from "../fieldEdit/GrassTypeDropdown";
import EnterDate from "../general/EnterDate";
import SeedingMethodDropdown from "./ReseedMethodDropDown";

/** Multipliers to convert a reseed total amount into kilograms. */
const RESEED_UNIT_TO_KG: Record<RESEED_TOTAL_AMOUNT_UNIT, number> = {
  [RESEED_TOTAL_AMOUNT_UNIT.G]: 0.001,
  [RESEED_TOTAL_AMOUNT_UNIT.KG]: 1,
  [RESEED_TOTAL_AMOUNT_UNIT.TONNE]: 1000,
};

const roundAmount = (n: number) => Math.round(n * 100) / 100;

const cardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  p: 2.5,
};

interface ReseedSingleStepProps {
  reseedDate: Date | null;
  setReseedDate: Dispatch<SetStateAction<Date | null>>;
  reseedDateError: boolean;
  setReseedDateError: Dispatch<SetStateAction<boolean>>;
  selectedFields: NameAndGuid[];
  setSelectedFields: Dispatch<SetStateAction<Array<NameAndGuid>>>;
  fieldsSelectInputError: boolean;
  setFieldsSelectInputError: Dispatch<SetStateAction<boolean>>;
  enableFieldSelection: boolean;
  onSeedingMethodChange: (method: SEEDING_METHOD | undefined) => void;
  newGrassType?: string;
  setNewGrassType: Dispatch<SetStateAction<GRASS_TYPE | undefined>>;
  grassTypeError: boolean;
  setGrassTypeError: Dispatch<SetStateAction<boolean>>;
  seedingMethod?: string;
  seedingMethodError: boolean;
  productName: string;
  setProductName: Dispatch<SetStateAction<string>>;
  seedingRate: number;
  setSeedingRate: Dispatch<SetStateAction<number>>;
  amountInputMode: AMOUNT_INPUT_MODE;
  setAmountInputMode: Dispatch<SetStateAction<AMOUNT_INPUT_MODE>>;
  totalAmount: number;
  setTotalAmount: Dispatch<SetStateAction<number>>;
  totalAmountUnit: RESEED_TOTAL_AMOUNT_UNIT;
  setTotalAmountUnit: Dispatch<SetStateAction<RESEED_TOTAL_AMOUNT_UNIT>>;
  productCost: number;
  setProductCost: Dispatch<SetStateAction<number>>;
  notes: string;
  setNotes: Dispatch<SetStateAction<string>>;
  fileUploadSlot?: ReactNode;
}

export function ReseedSingleStep({
  reseedDate,
  setReseedDate,
  reseedDateError,
  selectedFields,
  setSelectedFields,
  fieldsSelectInputError,
  setFieldsSelectInputError,
  enableFieldSelection,
  onSeedingMethodChange,
  newGrassType,
  setNewGrassType,
  grassTypeError,
  setGrassTypeError,
  seedingMethod,
  seedingMethodError,
  productName,
  setProductName,
  seedingRate,
  setSeedingRate,
  amountInputMode,
  setAmountInputMode,
  totalAmount,
  setTotalAmount,
  totalAmountUnit,
  setTotalAmountUnit,
  productCost,
  setProductCost,
  notes,
  setNotes,
  fileUploadSlot,
}: ReseedSingleStepProps) {
  const { t } = useTranslation();
  const { data: allFields = [] } = useFields();

  const isRateMode = amountInputMode === AMOUNT_INPUT_MODE.RATE;

  // Combined area (ha) of the currently selected fields.
  const sumAreaHa = (fields: NameAndGuid[]): number =>
    allFields
      .filter((f) => fields.some((sf) => sf.guid === f.guid))
      .reduce(
        (acc, f) => (f.invalidatedTimestamp ? acc : acc + f.area / 10000),
        0
      );
  const selectedAreaHa = sumAreaHa(selectedFields);

  // Derive the seeding rate (kg/ha) from a total amount + unit, for the given area.
  const deriveRateFromTotal = (
    total: number,
    unit: RESEED_TOTAL_AMOUNT_UNIT,
    areaHa: number
  ): number | null => totalToRatePerHa(total * RESEED_UNIT_TO_KG[unit], areaHa);

  const handleFieldToggle = (field: NameAndGuid) => {
    if (!enableFieldSelection) return;
    const isSelected = selectedFields.some((f) => f.guid === field.guid);
    const updated = isSelected
      ? selectedFields.filter((f) => f.guid !== field.guid)
      : [...selectedFields, field];
    setSelectedFields(updated);
    setFieldsSelectInputError(updated.length === 0);

    // Re-sync the amounts for the new area (rate stays fixed in RATE mode,
    // total stays fixed in TOTAL mode).
    const newArea = sumAreaHa(updated);
    if (isRateMode) {
      if (seedingRate !== undefined && (seedingRate as unknown) !== "") {
        setTotalAmount(
          roundAmount(ratePerHaToTotal(Number(seedingRate), newArea))
        );
        setTotalAmountUnit(RESEED_TOTAL_AMOUNT_UNIT.KG);
      }
    } else {
      const rate = deriveRateFromTotal(
        Number(totalAmount),
        totalAmountUnit,
        newArea
      );
      if (rate !== null) setSeedingRate(roundAmount(rate));
    }
  };

  const handleAmountModeChange = (mode: AMOUNT_INPUT_MODE | null) => {
    if (!mode || mode === amountInputMode) return;
    setAmountInputMode(mode);
    if (mode === AMOUNT_INPUT_MODE.RATE) {
      const rate = deriveRateFromTotal(
        Number(totalAmount),
        totalAmountUnit,
        selectedAreaHa
      );
      if (rate !== null) setSeedingRate(roundAmount(rate));
    } else {
      // Express the current rate as a total in kg.
      if (seedingRate !== undefined && (seedingRate as unknown) !== "") {
        setTotalAmount(
          roundAmount(ratePerHaToTotal(Number(seedingRate), selectedAreaHa))
        );
        setTotalAmountUnit(RESEED_TOTAL_AMOUNT_UNIT.KG);
      }
    }
  };

  const handleRateChange = (raw: string) => {
    const num = raw === "" ? ("" as unknown as number) : +raw;
    setSeedingRate(num);
    if (raw !== "" && selectedAreaHa > 0) {
      setTotalAmount(
        roundAmount(ratePerHaToTotal(Number(num), selectedAreaHa))
      );
      setTotalAmountUnit(RESEED_TOTAL_AMOUNT_UNIT.KG);
    }
  };

  const handleTotalChange = (raw: string) => {
    const num = raw === "" ? ("" as unknown as number) : +raw;
    setTotalAmount(num);
    if (raw !== "") {
      const rate = deriveRateFromTotal(
        Number(num),
        totalAmountUnit,
        selectedAreaHa
      );
      if (rate !== null) setSeedingRate(roundAmount(rate));
    }
  };

  const handleTotalUnitChange = (unit: RESEED_TOTAL_AMOUNT_UNIT) => {
    setTotalAmountUnit(unit);
    if ((totalAmount as unknown) !== "") {
      const rate = deriveRateFromTotal(
        Number(totalAmount),
        unit,
        selectedAreaHa
      );
      if (rate !== null) setSeedingRate(roundAmount(rate));
    }
  };

  const hasArea = selectedAreaHa > 0;
  const rateHelper =
    isRateMode && hasArea && (seedingRate as unknown) !== ""
      ? t("reseedEvent.amount.rateHelper", {
          total: roundAmount(
            ratePerHaToTotal(Number(seedingRate), selectedAreaHa)
          ),
          area: roundAmount(selectedAreaHa),
        })
      : "";
  const totalHelper =
    !isRateMode && hasArea && (totalAmount as unknown) !== ""
      ? (() => {
          const r = deriveRateFromTotal(
            Number(totalAmount),
            totalAmountUnit,
            selectedAreaHa
          );
          return r !== null
            ? t("reseedEvent.amount.totalHelper", {
                rate: roundAmount(r),
                area: roundAmount(selectedAreaHa),
              })
            : "";
        })()
      : "";

  return (
    <Container style={{ textAlign: "left" }}>
      <Grid container spacing={2.5}>
        {/* Date, Seeding Method & Grass Type */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("reseed.date.method.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <EnterDate
                  disabled={false}
                  date={reseedDate}
                  setDate={setReseedDate}
                  dateError={reseedDateError}
                  name={"reseedDate"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SeedingMethodDropdown
                  onChange={(value?: SEEDING_METHOD) => {
                    onSeedingMethodChange(value);
                  }}
                  seedingMethod={seedingMethod ?? ""}
                  seedingMethodInputError={seedingMethodError}
                  label={"reseedEvent.seedingMethod.text"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <GrassTypeDropdown
                  disabled={false}
                  onChange={(value?: GRASS_TYPE) => {
                    setNewGrassType(value);
                    if (value) {
                      setGrassTypeError(false);
                    }
                  }}
                  currentGrassType={newGrassType ?? ""}
                  grassTypeInputError={grassTypeError}
                  label={"reseedEvent.grassType.text"}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Field selection checkbox list */}
        <Grid size={12}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: fieldsSelectInputError ? "error.main" : undefined,
              fontWeight: 500,
            }}
          >
            {t("reseedEvents.selectFields.text")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              borderRadius: "8px",
              border: "1px solid",
              borderColor: fieldsSelectInputError
                ? "error.main"
                : "rgba(0, 0, 0, 0.12)",
              backgroundColor: "white",
              maxHeight: 220,
              overflowY: "auto",
              py: 0.5,
              px: 1,
              opacity: enableFieldSelection ? 1 : 0.5,
            }}
          >
            {[...allFields]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((field: Field) => {
                const isSelected = selectedFields.some(
                  (f) => f.guid === field.guid
                );
                return (
                  <FormControlLabel
                    key={field.guid}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() =>
                          handleFieldToggle({
                            guid: field.guid,
                            name: field.name,
                          })
                        }
                        size="small"
                        sx={{
                          color: "rgba(0,0,0,0.3)",
                          "&.Mui-checked": { color: "#7EC254" },
                        }}
                      />
                    }
                    label={field.name}
                    sx={{
                      mx: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.875rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                );
              })}
          </Box>
        </Grid>

        {/* Amount applied */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("reseedEvent.amount.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={amountInputMode}
                  onChange={(_, mode) =>
                    handleAmountModeChange(mode as AMOUNT_INPUT_MODE | null)
                  }
                >
                  <ToggleButton value={AMOUNT_INPUT_MODE.TOTAL}>
                    {t("fertilizerEvent.amountMode.total")}
                  </ToggleButton>
                  <ToggleButton value={AMOUNT_INPUT_MODE.RATE}>
                    {t("reseedEvent.amountMode.rate")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              {isRateMode ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    value={seedingRate}
                    type="number"
                    id="seedingRate"
                    fullWidth
                    error={seedingRate !== undefined && seedingRate < 0}
                    helperText={rateHelper}
                    onChange={(event) => handleRateChange(event.target.value)}
                    label={t("reseedEvent.seedingRate.label")}
                    name={`seedingRate`}
                  />
                </Grid>
              ) : (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      value={totalAmount}
                      type="number"
                      id="totalAmount"
                      fullWidth
                      error={totalAmount !== undefined && totalAmount < 0}
                      helperText={totalHelper}
                      onChange={(event) =>
                        handleTotalChange(event.target.value)
                      }
                      label={t("reseedEvent.totalAmount.label")}
                      name={`totalAmount`}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      fullWidth
                      value={totalAmountUnit}
                      onChange={(event) =>
                        handleTotalUnitChange(
                          event.target.value as RESEED_TOTAL_AMOUNT_UNIT
                        )
                      }
                      label={t("reseedEvent.totalAmountUnit.label")}
                      name={`totalAmountUnit`}
                    >
                      {Object.values(RESEED_TOTAL_AMOUNT_UNIT).map((u) => (
                        <MenuItem key={u} value={u}>
                          {t(`reseedEvent.totalAmountUnit.label.${u}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Additional details (optional) */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("common.additionalDetails.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  value={productName}
                  type="text"
                  id="productName"
                  fullWidth
                  onChange={(event) => {
                    setProductName(event.target.value);
                  }}
                  label={t("reseedEvent.productName.label")}
                  maxRows={1}
                  name={`productName`}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  value={productCost}
                  type="number"
                  id="productCost"
                  fullWidth
                  error={productCost !== undefined && productCost < 0}
                  onChange={(event) => {
                    if (event.target.value !== "") {
                      setProductCost(+event.target.value);
                    } else {
                      setProductCost(event.target.value as unknown as number);
                    }
                  }}
                  label={t("reseedEvent.productCost.label")}
                  maxRows={1}
                  name={`productCost`}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={notes}
                  id="reseedNotes"
                  fullWidth
                  onChange={(event) => {
                    setNotes(event.target.value);
                  }}
                  label={t("reseedEvent.notes.label")}
                  multiline
                  maxRows={12}
                />
              </Grid>
              {fileUploadSlot && <Grid size={12}>{fileUploadSlot}</Grid>}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
